/* eslint-disable unicode-bom */

// ============================================================================
// SuperCoins.js — ISHMAI Admin Panel
// ----------------------------------------------------------------------------
// Restructured to match the reference "Super Coins Management" screens:
// three top-level tabs — Overview / Settings / Customers — replacing the old
// single dashboard view. Overview shows the 4 stat cards plus a Settings
// Summary card and a Top-5 Customers card. Settings opens a dedicated full
// settings form. Customers opens the full customer table (search, filter,
// pagination, Add Super Coins) and can drill into Add Super Coins / Customer
// Super Coins Details as nested sub-views of that same tab.
//
// Nothing about the API contract or data-fetching logic below has changed —
// only the navigation/layout around it. See the original API CONTRACT notes:
//
//   getSuperCoinsSummary()              GET /api/admin/super-coins/summary
//   getSuperCoinSettings()/updateSuperCoinSettings(payload)
//                                        GET/PUT /api/admin/super-coins/settings
//   getCustomerSuperCoins({search,page,limit})
//                                        GET /api/admin/super-coins/customers
//   getCustomerSuperCoinDetails(id)      GET /api/admin/super-coins/customers/:id
//   getCustomerSuperCoinHistory(id,{page,limit,from,to})
//                                        GET /api/admin/super-coins/customers/:id/history
//   addCustomerSuperCoins({customerId,coins,reason})
//                                        POST /api/admin/super-coins/customers/:id/add
//
// NOTE ON NAVIGATION: Overview / Settings / Customers are switched with local
// state (`activeTab`), and Add Super Coins / Customer Details are nested
// sub-views of the Customers tab (`customerView`). If you'd rather have real
// URLs (e.g. /admin/super-coins/settings, /admin/super-coins/customers,
// /admin/super-coins/customers/add, /admin/super-coins/customers/:id), lift
// `activeTab` / `customerView` / `activeCustomerId` into your router and pass
// them in as props — the render functions below don't need to change either
// way.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import UpdateSuccessModal from '../components/UpdateSuccessModal';
import {
  getSuperCoinsDashboard,
  getSuperCoinsSummary,
  getSuperCoinSettings,
  updateSuperCoinSettings,
  getCustomerSuperCoins,
  getCustomerSuperCoinDetails,
  getCustomerSuperCoinHistory,
  addCustomerSuperCoins,
  adjustCustomerSuperCoins,
  getSuperCoinTransactions,
  refundSuperCoins,
} from '../services/api';
import './supercoins.css';

// ---------------------------------------------------------------------------
// Constants / helpers
// ---------------------------------------------------------------------------

var CUSTOMERS_PER_PAGE_OPTIONS = [10, 25, 50];
var HISTORY_PER_PAGE = 10;
var DEFAULT_COINS_PER_RUPEE = 10;
var TOP_CUSTOMERS_LIMIT = 5;

var formatCoins = function (n) { return Number(n || 0).toLocaleString('en-IN'); };
var formatINR = function (n) {
  var num = Number(n || 0);
  return '\u20B9' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
var coinsToRupees = function (coins, coinsPerRupee) {
  var c = Number(coins || 0);
  var rate = Number(coinsPerRupee) || DEFAULT_COINS_PER_RUPEE;
  return rate ? c / rate : 0;
};
var formatDateShort = function (d) {
  if (!d) return '\u2014';
  var dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
var formatDateTime = function (d) {
  if (!d) return '\u2014';
  var dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    + ', ' + dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};
var totalPages = function (count, perPage) { return Math.max(1, Math.ceil((count || 0) / perPage)); };

var getErrorMessage = function (err, fallback) {
  if (!err) return fallback || 'An unexpected error occurred';
  var data = err.response && err.response.data;
  if (typeof data === 'string') {
    if (data.includes('Cannot POST') || data.includes('Cannot GET') || data.includes('<!DOCTYPE') || data.includes('<html')) {
      return 'The requested server endpoint was not found or is unavailable.';
    }
    return data;
  }
  if (data && typeof data === 'object') {
    if (data.message) return data.message;
    if (data.msg) return data.msg;
    if (data.error) return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
  }
  return err.message || fallback || 'An unexpected error occurred';
};

var getCustId = function (c) {
  if (!c) return null;
  if (c.customerId && typeof c.customerId === 'object') return c.customerId._id || c.customerId.id;
  return c.customerId || c._id || c.id || null;
};

var getCustName = function (c) {
  if (!c) return 'Customer';
  if (c.customerName && c.customerName !== 'Customer') return c.customerName;
  var custObj = (c.customerId && typeof c.customerId === 'object') ? c.customerId : c;
  var name = custObj.fullName || custObj.name || c.fullName || c.name;
  var phone = custObj.phone || custObj.mobileNumber || c.phone || c.mobileNumber;
  if (name && String(name).trim()) return name;
  if (phone && String(phone).trim()) return 'Customer (' + phone + ')';
  return 'Customer';
};

var getCustEmail = function (c) {
  if (!c) return '—';
  if (c.email && c.email !== '-') return c.email;
  var custObj = (c.customerId && typeof c.customerId === 'object') ? c.customerId : c;
  if (custObj.email && custObj.email !== '-') return custObj.email;
  var phone = custObj.phone || custObj.mobileNumber || c.phone || c.mobileNumber;
  if (phone && String(phone).trim()) return phone;
  return '—';
};

var getCustAvailable = function (c) {
  if (!c) return 0;
  if (c.availableSuperCoins !== undefined) return Number(c.availableSuperCoins || 0);
  if (c.availableCoins !== undefined) return Number(c.availableCoins || 0);
  if (c.coins !== undefined) return Number(c.coins || 0);
  return 0;
};

var getCustValue = function (c, rate) {
  if (!c) return 0;
  if (c.coinValue !== undefined) return Number(c.coinValue || 0);
  if (c.rupeeValue !== undefined) return Number(c.rupeeValue || 0);
  return coinsToRupees(getCustAvailable(c), rate);
};

var getCustEarned = function (c) {
  if (!c) return 0;
  return Number(c.totalEarned || 0);
};

var getCustUsed = function (c) {
  if (!c) return 0;
  return Number(c.totalUsed || 0);
};

var getCustAmountUsed = function (c, rate) {
  if (!c) return 0;
  if (c.totalUsedRupeeValue !== undefined) return Number(c.totalUsedRupeeValue || 0);
  if (c.amountUsed !== undefined) return Number(c.amountUsed || 0);
  return coinsToRupees(getCustUsed(c), rate);
};

var getCustRegReward = function (c) {
  if (!c) return 0;
  return Number(c.registrationReward || 0);
};

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

var Icon = function (props) {
  var children = props.children; var size = props.size || 15;
  return React.createElement('svg', {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
  }, children);
};

var CoinIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('circle', { cx: 12, cy: 12, r: 9 }),
    React.createElement('path', { d: 'M9.5 9.5c0-1 .9-1.8 2.5-1.8s2.5.8 2.5 1.6c0 2.2-5 1.4-5 3.7 0 .9 1 1.6 2.5 1.6s2.5-.7 2.5-1.7' }),
    React.createElement('line', { x1: 12, y1: 6.2, x2: 12, y2: 7.7 }),
    React.createElement('line', { x1: 12, y1: 16.3, x2: 12, y2: 17.8 })
  );
};

var SwapIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('polyline', { points: '17 1 21 5 17 9' }),
    React.createElement('path', { d: 'M3 11V9a4 4 0 0 1 4-4h14' }),
    React.createElement('polyline', { points: '7 23 3 19 7 15' }),
    React.createElement('path', { d: 'M21 13v2a4 4 0 0 1-4 4H3' })
  );
};

var UsersIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('path', { d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' }),
    React.createElement('circle', { cx: 9, cy: 7, r: 4 }),
    React.createElement('path', { d: 'M23 21v-2a4 4 0 0 0-3-3.87' }),
    React.createElement('path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' })
  );
};

var GiftIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('polyline', { points: '20 12 20 22 4 22 4 12' }),
    React.createElement('rect', { x: 2, y: 7, width: 20, height: 5 }),
    React.createElement('line', { x1: 12, y1: 22, x2: 12, y2: 7 }),
    React.createElement('path', { d: 'M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z' }),
    React.createElement('path', { d: 'M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z' })
  );
};

var SearchIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('circle', { cx: 11, cy: 11, r: 8 }),
    React.createElement('line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 })
  );
};

var FilterIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('polygon', { points: '22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3' })
  );
};

var PlusIcon = function () {
  return React.createElement(Icon, { size: 15 },
    React.createElement('line', { x1: 12, y1: 5, x2: 12, y2: 19 }),
    React.createElement('line', { x1: 5, y1: 12, x2: 19, y2: 12 })
  );
};

var ArrowLeftIcon = function () {
  return React.createElement(Icon, { size: 15 },
    React.createElement('line', { x1: 19, y1: 12, x2: 5, y2: 12 }),
    React.createElement('polyline', { points: '12 19 5 12 12 5' })
  );
};

var ChevronLeftIcon = function () {
  return React.createElement(Icon, { size: 15 }, React.createElement('polyline', { points: '15 18 9 12 15 6' }));
};

var ChevronRightIcon = function () {
  return React.createElement(Icon, { size: 15 }, React.createElement('polyline', { points: '9 18 15 12 9 6' }));
};

var CheckCircleIcon = function () {
  return React.createElement(Icon, { size: 16 },
    React.createElement('path', { d: 'M22 11.08V12a10 10 0 1 1-5.93-9.14' }),
    React.createElement('polyline', { points: '22 4 12 14.01 9 11.01' })
  );
};

var AlertCircleIcon = function () {
  return React.createElement(Icon, { size: 16 },
    React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
    React.createElement('line', { x1: 12, y1: 8, x2: 12, y2: 12 }),
    React.createElement('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })
  );
};

var XIcon = function () {
  return React.createElement(Icon, { size: 16 },
    React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
};

var GearIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('circle', { cx: 12, cy: 12, r: 3 }),
    React.createElement('path', { d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' })
  );
};

var ClockIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('circle', { cx: 12, cy: 12, r: 9 }),
    React.createElement('polyline', { points: '12 7 12 12 15.5 14' })
  );
};

var PercentIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('line', { x1: 19, y1: 5, x2: 5, y2: 19 }),
    React.createElement('circle', { cx: 6.5, cy: 6.5, r: 2.5 }),
    React.createElement('circle', { cx: 17.5, cy: 17.5, r: 2.5 })
  );
};

// ---------------------------------------------------------------------------
// Small reusable primitives
// ---------------------------------------------------------------------------

var Toast = function (props) {
  var toast = props.toast; var onClose = props.onClose;
  useEffect(function () {
    if (!toast) return undefined;
    var t = setTimeout(onClose, 3500);
    return function () { clearTimeout(t); };
  }, [toast, onClose]);
  if (!toast) return null;
  return React.createElement('div', { className: 'sc-toast sc-toast-' + toast.type },
    toast.type === 'success' ? React.createElement(CheckCircleIcon, null) : React.createElement(AlertCircleIcon, null),
    React.createElement('span', null, toast.message),
    React.createElement('button', { className: 'sc-toast-close', type: 'button', onClick: onClose }, React.createElement(XIcon, null))
  );
};

var Pagination = function (props) {
  var page = props.page; var pages = props.pages; var onChange = props.onChange;
  if (pages <= 1) return null;
  var nums = [];
  for (var i = 1; i <= pages; i++) nums.push(i);
  var visible = nums.slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5);
  return React.createElement('div', { className: 'sc-pagination' },
    React.createElement('button', { className: 'sc-page-btn', type: 'button', disabled: page <= 1, onClick: function () { onChange(page - 1); } }, React.createElement(ChevronLeftIcon, null)),
    visible.map(function (p) {
      return React.createElement('button', {
        key: p, className: 'sc-page-btn' + (p === page ? ' is-active' : ''),
        onClick: function () { onChange(p); },
      }, p);
    }),
    React.createElement('button', { className: 'sc-page-btn', type: 'button', disabled: page >= pages, onClick: function () { onChange(page + 1); } }, React.createElement(ChevronRightIcon, null))
  );
};

var SummaryCard = function (props) {
  return React.createElement('div', { className: 'sc-summary-card' },
    React.createElement('div', { className: 'sc-card-icon ' + (props.iconClass || '') }, props.icon),
    React.createElement('div', { className: 'sc-card-content' },
      React.createElement('div', { className: 'sc-card-label' }, props.label),
      React.createElement('div', { className: 'sc-card-val' }, props.value),
      props.trend !== undefined && props.trend !== null && React.createElement('div', { className: 'sc-card-trend' },
        '\u2191 ' + props.trend + '% this month')
    )
  );
};

var Breadcrumb = function (props) {
  return React.createElement('div', { className: 'sc-breadcrumb' },
    props.items.map(function (item, i) {
      return React.createElement(React.Fragment, { key: i },
        i > 0 && React.createElement('span', { className: 'sc-breadcrumb-sep' }, '\u203A'),
        item.onClick
          ? React.createElement('button', { type: 'button', className: 'sc-breadcrumb-link', onClick: item.onClick }, item.label)
          : React.createElement('span', { className: 'sc-breadcrumb-current' }, item.label)
      );
    })
  );
};

// -- top-level tab bar (Overview / Settings / Customers) --

var TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'settings', label: 'Settings' },
  { key: 'customers', label: 'Customers' },
];

var TabBar = function (props) {
  var active = props.active; var onChange = props.onChange;
  return React.createElement('div', { className: 'sc-tabs' },
    TABS.map(function (t) {
      return React.createElement('button', {
        key: t.key, type: 'button',
        className: 'sc-tab' + (active === t.key ? ' is-active' : ''),
        onClick: function () { onChange(t.key); },
      }, t.label);
    })
  );
};

// -- one row inside the Settings Summary card --

var SettingsSummaryRow = function (props) {
  return React.createElement('div', { className: 'sc-summary-row' },
    React.createElement('div', { className: 'sc-summary-row-label' },
      React.createElement('span', { className: 'sc-summary-row-icon' }, props.icon),
      props.label
    ),
    props.badge
      ? React.createElement('span', { className: 'sc-status-badge ' + (props.badgeOn ? 'is-on' : 'is-off') }, props.value)
      : React.createElement('span', { className: 'sc-summary-row-value' }, props.value)
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

var SuperCoins = function () {
  // top-level tab: 'overview' | 'settings' | 'customers'
  var activeTabState = useState('overview'); var activeTab = activeTabState[0]; var setActiveTab = activeTabState[1];
  // sub-view inside the Customers tab: 'list' | 'add' | 'details'
  var customerViewState = useState('list'); var customerView = customerViewState[0]; var setCustomerView = customerViewState[1];
  var activeCustomerIdState = useState(null); var activeCustomerId = activeCustomerIdState[0]; var setActiveCustomerId = activeCustomerIdState[1];
  var activeCustomerDataState = useState(null); var activeCustomerData = activeCustomerDataState[0]; var setActiveCustomerData = activeCustomerDataState[1];

  var toastState = useState(null); var toast = toastState[0]; var setToast = toastState[1];
  var showToast = function (type, message) { setToast({ type: type, message: message }); };

  var successModalState = useState(null); var successModal = successModalState[0]; var setSuccessModal = successModalState[1];

  var goToTab = function (tab) {
    setActiveTab(tab);
    if (tab === 'customers') setCustomerView('list');
  };
  var goToSettings = function () { goToTab('settings'); };
  var goToCustomersList = function () { setActiveTab('customers'); setCustomerView('list'); setActiveCustomerId(null); setActiveCustomerData(null); };
  var goToAdd = function () { setActiveTab('customers'); setCustomerView('add'); };
  var goToDetails = function (custOrId) {
    var id = (custOrId && typeof custOrId === 'object') ? getCustId(custOrId) : custOrId;
    var data = (custOrId && typeof custOrId === 'object') ? custOrId : null;
    setActiveTab('customers');
    setActiveCustomerId(id);
    setActiveCustomerData(data);
    setCustomerView('details');
  };

  // -- summary --
  var summaryState = useState(null); var summary = summaryState[0]; var setSummary = summaryState[1];

  // -- settings --
  var settingsState = useState(null); var settings = settingsState[0]; var setSettings = settingsState[1];
  var settingsLoadingState = useState(true); var settingsLoading = settingsLoadingState[0]; var setSettingsLoading = settingsLoadingState[1];
  var settingsSavingState = useState(false); var settingsSaving = settingsSavingState[0]; var setSettingsSaving = settingsSavingState[1];
  var settingsErrorState = useState(''); var settingsError = settingsErrorState[0]; var setSettingsError = settingsErrorState[1];
  var initialSettingsRef = useRef(null);

  var coinsPerRupee = (settings && settings.coinsPerRupee) || DEFAULT_COINS_PER_RUPEE;

  // -- top 5 customers (Overview tab) --
  var topCustomersState = useState([]); var topCustomers = topCustomersState[0]; var setTopCustomers = topCustomersState[1];
  var topCustomersLoadingState = useState(true); var topCustomersLoading = topCustomersLoadingState[0]; var setTopCustomersLoading = topCustomersLoadingState[1];

  // -- full customers table (Customers tab) --
  var customersState = useState([]); var customers = customersState[0]; var setCustomers = customersState[1];
  var customersTotalState = useState(0); var customersTotal = customersTotalState[0]; var setCustomersTotal = customersTotalState[1];
  var customersLoadingState = useState(true); var customersLoading = customersLoadingState[0]; var setCustomersLoading = customersLoadingState[1];
  var customersErrorState = useState(''); var customersError = customersErrorState[0]; var setCustomersError = customersErrorState[1];
  var searchState = useState(''); var search = searchState[0]; var setSearch = searchState[1];
  var searchDebState = useState(''); var searchDeb = searchDebState[0]; var setSearchDeb = searchDebState[1];
  var pageState = useState(1); var page = pageState[0]; var setPage = pageState[1];
  var perPageState = useState(10); var perPage = perPageState[0]; var setPerPage = perPageState[1];

  var fetchSummary = useCallback(function () {
    var fn = typeof getSuperCoinsDashboard === 'function' ? getSuperCoinsDashboard : getSuperCoinsSummary;
    if (typeof fn !== 'function') return;
    try {
      var req = fn();
      if (!req || typeof req.then !== 'function') return;
      req
        .then(function (res) {
          var d = (res && res.data && res.data.data) ? res.data.data : (res && res.data) || {};
          setSummary(d);
          if (Array.isArray(d.topCustomers) && d.topCustomers.length > 0) {
            setTopCustomers(d.topCustomers);
            setTopCustomersLoading(false);
          }
        })
        .catch(function (e) { console.error('Failed to load Super Coins summary:', e); });
    } catch (err) {
      console.error('Error invoking summary function:', err);
    }
  }, []);

  var fetchSettings = useCallback(function () {
    if (typeof getSuperCoinSettings !== 'function') return;
    setSettingsLoading(true);
    try {
      var req = getSuperCoinSettings();
      if (!req || typeof req.then !== 'function') {
        setSettingsLoading(false);
        return;
      }
      req
        .then(function (res) {
          var d = (res && res.data && res.data.data) ? res.data.data : (res && res.data) || {};
          var maxDisc = d.maxDiscountPercentage !== undefined ? d.maxDiscountPercentage : (d.maxDiscountPercent !== undefined ? d.maxDiscountPercent : '');
          var loaded = {
            coinsPerRupee: d.coinsPerRupee !== undefined ? String(d.coinsPerRupee) : '10',
            superCoinsPerRupee: d.superCoinsPerRupee !== undefined ? String(d.superCoinsPerRupee) : (d.coinsPerRupee !== undefined ? String(d.coinsPerRupee) : '10'),
            registrationReward: d.registrationReward !== undefined ? String(d.registrationReward) : '100',
            enableRegistrationReward: d.enableRegistrationReward !== false,
            referralReward: d.referralReward !== undefined ? String(d.referralReward) : (d.referralCoins !== undefined ? String(d.referralCoins) : '50'),
            referralCoins: d.referralReward !== undefined ? String(d.referralReward) : (d.referralCoins !== undefined ? String(d.referralCoins) : '50'),
            maxCoinsPerOrder: d.maxCoinsPerOrder !== undefined ? String(d.maxCoinsPerOrder) : '1000',
            maxDiscountPercentage: maxDisc !== undefined ? String(maxDisc) : '20',
            conversionRate: (d.conversionRate ? String(d.conversionRate).replace(/Super Coins/gi, 'ISHMAI Coins') : ((d.coinsPerRupee || 10) + ' ISHMAI Coins = \u20B91'))
          };
          setSettings(loaded);
          initialSettingsRef.current = Object.assign({}, loaded);
          setSettingsError('');
          setSettingsLoading(false);
        })
        .catch(function (e) {
          setSettingsError(getErrorMessage(e, 'Failed to load settings.'));
          var fallback = {
            coinsPerRupee: '10',
            superCoinsPerRupee: '10',
            registrationReward: '100',
            enableRegistrationReward: true,
            referralReward: '50',
            referralCoins: '50',
            maxCoinsPerOrder: '1000',
            maxDiscountPercentage: '20',
            conversionRate: '10 ISHMAI Coins = \u20B91'
          };
          setSettings(fallback);
          initialSettingsRef.current = Object.assign({}, fallback);
          setSettingsLoading(false);
        });
    } catch (err) {
      setSettingsLoading(false);
    }
  }, []);

  var fetchTopCustomers = useCallback(function () {
    if (typeof getCustomerSuperCoins !== 'function') return;
    setTopCustomersLoading(true);
    try {
      var req = getCustomerSuperCoins({ page: 1, limit: TOP_CUSTOMERS_LIMIT });
      if (!req || typeof req.then !== 'function') {
        setTopCustomersLoading(false);
        return;
      }
      req
        .then(function (res) {
          var body = res && res.data;
          var list = (body && Array.isArray(body.data)) ? body.data : ((body && body.data && body.data.list) || (Array.isArray(body) ? body : []));
          setTopCustomers(list.slice(0, TOP_CUSTOMERS_LIMIT));
          setTopCustomersLoading(false);
        })
        .catch(function (e) {
          console.error('Failed to load top Super Coins customers:', e);
          setTopCustomers([]);
          setTopCustomersLoading(false);
        });
    } catch (err) {
      setTopCustomersLoading(false);
    }
  }, []);

  var fetchCustomers = useCallback(function () {
    if (typeof getCustomerSuperCoins !== 'function') return;
    setCustomersLoading(true);
    setCustomersError('');
    try {
      var req = getCustomerSuperCoins({ search: searchDeb || undefined, page: page, limit: perPage });
      if (!req || typeof req.then !== 'function') {
        setCustomersLoading(false);
        return;
      }
      req
        .then(function (res) {
          var body = res && res.data;
          var list = (body && Array.isArray(body.data)) ? body.data : ((body && body.data && body.data.list) || (Array.isArray(body) ? body : []));
          var totalCount = (body && body.total !== undefined) ? body.total : list.length;
          setCustomers(list);
          setCustomersTotal(totalCount);
          setCustomersLoading(false);
        })
        .catch(function (e) {
          console.error('Failed to load customer Super Coin balances:', e);
          setCustomersError('Failed to load customers. Please try again.');
          setCustomers([]);
          setCustomersLoading(false);
        });
    } catch (err) {
      setCustomersLoading(false);
    }
  }, [searchDeb, page, perPage]);

  useEffect(function () {
    fetchSummary();
    fetchSettings();
    fetchTopCustomers();
  }, [fetchSummary, fetchSettings, fetchTopCustomers]);
  useEffect(function () { fetchCustomers(); }, [fetchCustomers]);

  useEffect(function () {
    var t = setTimeout(function () { setPage(1); setSearchDeb(search); }, 400);
    return function () { clearTimeout(t); };
  }, [search]);

  var refreshAll = function () { fetchSummary(); fetchTopCustomers(); fetchCustomers(); };

  // -- settings form handlers --
  var updateSettingField = function (field, value) {
    setSettings(function (prev) { var next = Object.assign({}, prev); next[field] = value; return next; });
  };

  var handleResetSettings = function () { setSettingsError(''); fetchSettings(); };

  var handleSaveSettings = function () {
    if (!settings) return;
    var numeric = [
      ['coinsPerRupee', 'Coins Per \u20B91 (conversion rate)'],
      ['registrationReward', 'Registration Reward'],
      ['maxCoinsPerOrder', 'Maximum Coins Per Order'],
      ['maxDiscountPercentage', 'Maximum Discount Percentage'],
      ['referralReward', 'Referral Coins'],
    ];
    for (var i = 0; i < numeric.length; i++) {
      var val = settings[numeric[i][0]];
      if (val === '' || isNaN(Number(val)) || Number(val) < 0) {
        setSettingsError(numeric[i][1] + ' must be a valid positive number');
        return;
      }
    }
    setSettingsError('');
    setSettingsSaving(true);

    var initial = initialSettingsRef.current || {};
    var payload = {};

    var hasExistingChanges = (
      (initial.coinsPerRupee !== undefined && String(settings.coinsPerRupee) !== String(initial.coinsPerRupee)) ||
      (initial.registrationReward !== undefined && String(settings.registrationReward) !== String(initial.registrationReward)) ||
      (initial.enableRegistrationReward !== undefined && Boolean(settings.enableRegistrationReward) !== Boolean(initial.enableRegistrationReward)) ||
      (initial.maxCoinsPerOrder !== undefined && String(settings.maxCoinsPerOrder) !== String(initial.maxCoinsPerOrder)) ||
      (initial.maxDiscountPercentage !== undefined && String(settings.maxDiscountPercentage) !== String(initial.maxDiscountPercentage))
    );

    if (hasExistingChanges) {
      payload.coinsPerRupee = Number(settings.coinsPerRupee);
      payload.registrationReward = Number(settings.registrationReward);
      payload.enableRegistrationReward = Boolean(settings.enableRegistrationReward);
      payload.maxCoinsPerOrder = Number(settings.maxCoinsPerOrder);
      payload.maxDiscountPercentage = Number(settings.maxDiscountPercentage !== undefined ? settings.maxDiscountPercentage : settings.maxDiscountPercent);
    }

    var refVal = settings.referralReward !== undefined ? settings.referralReward : settings.referralCoins;
    if (refVal !== undefined && refVal !== '') {
      payload.referralReward = Number(refVal);
    }

    updateSuperCoinSettings(payload)
      .then(function (res) {
        var resData = (res && res.data && res.data.data) ? res.data.data : (res && res.data) || {};
        var updatedReferral = resData.referralReward !== undefined
          ? resData.referralReward
          : (resData.referralCoins !== undefined ? resData.referralCoins : payload.referralReward);

        if (updatedReferral !== undefined && updatedReferral !== null) {
          setSettings(function (prev) {
            var next = Object.assign({}, prev);
            next.referralReward = String(updatedReferral);
            next.referralCoins = String(updatedReferral);
            return next;
          });
          if (initialSettingsRef.current) {
            initialSettingsRef.current.referralReward = String(updatedReferral);
            initialSettingsRef.current.referralCoins = String(updatedReferral);
          }
        }

        var msg = ((res && res.data && (res.data.message || res.data.msg)) || 'ISHMAI Coins settings updated successfully').replace(/Super Coins/gi, 'ISHMAI Coins');
        showToast('success', msg);
        setSuccessModal({
          isOpen: true,
          message: 'Settings Saved Successfully',
          subtitle: msg,
        });
        fetchSettings();
        fetchSummary();
      })
      .catch(function (e) {
        var msg = getErrorMessage(e, 'Failed to save settings');
        showToast('error', msg);
      })
      .finally(function () { setSettingsSaving(false); });
  };

  var customersPages = totalPages(customersTotal, perPage);

  // =========================================================================
  // Overview tab
  // =========================================================================

  var renderOverview = function () {
    var totalIssuedVal = summary && (summary.totalIssued !== undefined ? summary.totalIssued : (summary.totalEarned !== undefined ? summary.totalEarned : null));
    var totalUsedVal = summary && (summary.totalUsed !== undefined ? summary.totalUsed : (summary.totalRedeemed !== undefined ? summary.totalRedeemed : null));
    var activeCustVal = summary && (summary.activeCustomers !== undefined ? summary.activeCustomers : (summary.totalCustomers !== undefined ? summary.totalCustomers : null));
    var regRewardsVal = summary && (summary.registrationRewardsGiven !== undefined ? summary.registrationRewardsGiven : (summary.registrationReward !== undefined ? summary.registrationReward : (summary.registrationRewards !== undefined ? summary.registrationRewards : null)));

    if (totalIssuedVal === null && customers.length > 0) {
      totalIssuedVal = customers.reduce(function (s, c) { return s + getCustEarned(c); }, 0);
    }
    if (totalUsedVal === null && customers.length > 0) {
      totalUsedVal = customers.reduce(function (s, c) { return s + getCustUsed(c); }, 0);
    }
    if (activeCustVal === null) {
      activeCustVal = customersTotal || customers.length;
    }
    if (regRewardsVal === null && customers.length > 0) {
      regRewardsVal = customers.reduce(function (s, c) { return s + getCustRegReward(c); }, 0);
    }

    return React.createElement(React.Fragment, null,

      // -- summary cards --
      React.createElement('div', { className: 'sc-summary-grid' },
        React.createElement(SummaryCard, {
          icon: React.createElement(CoinIcon, { size: 20 }), iconClass: 'sc-card-icon-issued',
          label: 'Total ISHMAI Coins Issued', value: formatCoins(totalIssuedVal), trend: summary && summary.totalIssuedTrend,
        }),
        React.createElement(SummaryCard, {
          icon: React.createElement(SwapIcon, { size: 20 }), iconClass: 'sc-card-icon-used',
          label: 'Total ISHMAI Coins Used', value: formatCoins(totalUsedVal), trend: summary && summary.totalUsedTrend,
        }),
        React.createElement(SummaryCard, {
          icon: React.createElement(UsersIcon, { size: 20 }), iconClass: 'sc-card-icon-customers',
          label: 'Active Customers', value: formatCoins(activeCustVal), trend: summary && summary.activeCustomersTrend,
        }),
        React.createElement(SummaryCard, {
          icon: React.createElement(GiftIcon, { size: 20 }), iconClass: 'sc-card-icon-reward',
          label: 'Registration Rewards Given', value: formatCoins(regRewardsVal), trend: summary && summary.registrationRewardsTrend,
        })
      ),

      React.createElement('div', { className: 'sc-overview-grid' },

        // -- settings summary --
        React.createElement('div', { className: 'sc-card sc-settings-summary-card' },
          React.createElement('div', { className: 'sc-card-hdr' },
            React.createElement('div', { className: 'sc-card-hdr-title' },
              React.createElement('span', { className: 'sc-card-hdr-icon' }, React.createElement(GearIcon, { size: 16 })),
              React.createElement('h3', null, 'ISHMAI Coins Settings (Summary)')
            )
          ),
          settingsLoading || !settings
            ? React.createElement('div', { className: 'sc-table-empty' }, 'Loading settings\u2026')
            : React.createElement(React.Fragment, null,
                React.createElement(SettingsSummaryRow, {
                  icon: React.createElement(CoinIcon, { size: 14 }),
                  label: 'Coins per \u20B91', value: formatCoins(settings.coinsPerRupee) + ' ISHMAI Coins',
                }),
                React.createElement(SettingsSummaryRow, {
                  icon: React.createElement(SwapIcon, { size: 14 }),
                  label: 'Conversion Rate', value: (settings.conversionRate ? String(settings.conversionRate).replace(/Super Coins/gi, 'ISHMAI Coins') : (formatCoins(settings.coinsPerRupee) + ' ISHMAI Coins = \u20B91')),
                }),
                React.createElement(SettingsSummaryRow, {
                  icon: React.createElement(GiftIcon, { size: 14 }),
                  label: 'Registration Reward', value: formatCoins(settings.registrationReward) + ' ISHMAI Coins',
                }),
                React.createElement(SettingsSummaryRow, {
                  icon: React.createElement(GiftIcon, { size: 14 }),
                  label: 'Registration Reward', badge: true, badgeOn: !!settings.enableRegistrationReward,
                  value: settings.enableRegistrationReward ? 'Enabled' : 'Disabled',
                }),
                React.createElement(SettingsSummaryRow, {
                  icon: React.createElement(ClockIcon, { size: 14 }),
                  label: 'Maximum Coins Per Order', value: formatCoins(settings.maxCoinsPerOrder) + ' ISHMAI Coins',
                }),
                React.createElement(SettingsSummaryRow, {
                  icon: React.createElement(PercentIcon, { size: 14 }),
                  label: 'Maximum Discount %', value: (settings.maxDiscountPercentage || settings.maxDiscountPercent || '0') + '%',
                }),
                React.createElement('div', { className: 'sc-form-actions sc-settings-summary-actions' },
                  React.createElement('button', { className: 'sc-btn sc-btn-primary', type: 'button', onClick: goToSettings },
                    React.createElement(GearIcon, { size: 14 }), 'Edit Settings')
                )
              )
        ),

        // -- top 5 customers --
        React.createElement('div', { className: 'sc-card sc-top-customers-card' },
          React.createElement('div', { className: 'sc-card-hdr' },
            React.createElement('div', { className: 'sc-card-hdr-title' },
              React.createElement('span', { className: 'sc-card-hdr-icon' }, React.createElement(UsersIcon, { size: 16 })),
              React.createElement('h3', null, 'Customer ISHMAI Coins (Top 5)')
            ),
            React.createElement('div', { className: 'sc-card-hdr-actions' },
              React.createElement('button', { className: 'sc-btn sc-btn-primary sc-btn-sm', type: 'button', onClick: goToAdd },
                React.createElement(PlusIcon, null), 'Add ISHMAI Coins'),
              React.createElement('button', { className: 'sc-btn sc-btn-ghost sc-btn-sm', type: 'button', onClick: goToCustomersList },
                'View All Customers')
            )
          ),
          React.createElement('div', { className: 'sc-table-scroll' },
            React.createElement('table', { className: 'tbl' },
              React.createElement('thead', null,
                React.createElement('tr', null,
                  React.createElement('th', null, 'S.No'),
                  React.createElement('th', null, 'Customer Name'),
                  React.createElement('th', null, 'Email'),
                  React.createElement('th', null, 'Available ISHMAI Coins'),
                  React.createElement('th', null, 'Coin Value (\u20B9)'),
                  React.createElement('th', null, 'Total Earned'),
                  React.createElement('th', null, 'Total Used'),
                  React.createElement('th', null, 'Action')
                )
              ),
              React.createElement('tbody', null,
                topCustomersLoading
                  ? React.createElement('tr', null, React.createElement('td', { colSpan: 8, className: 'sc-table-empty' }, 'Loading customers\u2026'))
                  : topCustomers.length === 0
                    ? React.createElement('tr', null, React.createElement('td', { colSpan: 8, className: 'sc-table-empty' }, 'No customers found.'))
                    : topCustomers.map(function (c, idx) {
                        var id = getCustId(c);
                        var available = getCustAvailable(c);
                        return React.createElement('tr', { key: id || idx },
                          React.createElement('td', null, idx + 1),
                          React.createElement('td', null, getCustName(c)),
                          React.createElement('td', null, getCustEmail(c)),
                          React.createElement('td', null, React.createElement('span', { className: 'sc-badge sc-badge-available' }, formatCoins(available))),
                          React.createElement('td', null, formatINR(getCustValue(c, coinsPerRupee))),
                          React.createElement('td', { className: 'sc-coins-pos' }, formatCoins(getCustEarned(c))),
                          React.createElement('td', { className: 'sc-coins-neg' }, formatCoins(getCustUsed(c))),
                          React.createElement('td', { className: 'actions-cell' },
                            React.createElement('button', {
                              className: 'sc-btn sc-btn-outline sc-btn-sm', type: 'button',
                              onClick: function () { goToDetails(c); },
                            }, 'View Details')
                          )
                        );
                      })
              )
            )
          )
        )
      )
    );
  };

  // =========================================================================
  // Settings tab
  // =========================================================================

  var renderSettingsTab = function () {
    return React.createElement(React.Fragment, null,
      React.createElement(Breadcrumb, { items: [{ label: 'ISHMAI Coins', onClick: function () { goToTab('overview'); } }, { label: 'Settings' }] }),

      React.createElement('div', { className: 'sc-card' },
        React.createElement('div', { className: 'sc-card-hdr' },
          React.createElement('div', { className: 'sc-card-hdr-title' },
            React.createElement('span', { className: 'sc-card-hdr-icon' }, React.createElement(GearIcon, { size: 16 })),
            React.createElement('h3', null, 'ISHMAI Coins Configuration')
          )
        ),
        settingsLoading || !settings
          ? React.createElement('div', { className: 'sc-table-empty' }, 'Loading settings\u2026')
          : React.createElement(React.Fragment, null,
              settingsError && React.createElement('div', { className: 'sc-form-error' }, React.createElement(AlertCircleIcon, null), settingsError),
              React.createElement('div', { className: 'sc-settings-grid' },
                React.createElement('div', { className: 'sc-field' },
                  React.createElement('label', null, 'Coins Per \u20B91 (Conversion Rate) *'),
                  React.createElement('input', {
                    type: 'number', min: 0, step: '0.01', placeholder: 'e.g. 10',
                    value: settings.coinsPerRupee,
                    onChange: function (e) { updateSettingField('coinsPerRupee', e.target.value); },
                  }),
                  React.createElement('span', { className: 'sc-field-hint' }, '1 Rupee = ' + (settings.coinsPerRupee || 0) + ' ISHMAI Coins')
                ),
                React.createElement('div', { className: 'sc-field' },
                  React.createElement('label', null, 'ISHMAI Coins Per \u20B91 (Same Rate)'),
                  React.createElement('input', {
                    type: 'number', min: 0, step: '0.01', placeholder: 'e.g. 10',
                    value: settings.coinsPerRupee, readOnly: true, className: 'sc-readonly',
                  }),
                  React.createElement('span', { className: 'sc-field-hint' }, '10 ISHMAI Coins = \u20B91 \u2014 kept in sync with the field above')
                ),
                React.createElement('div', { className: 'sc-field' },
                  React.createElement('label', null, 'Registration Reward (For New User) *'),
                  React.createElement('input', {
                    type: 'number', min: 0, step: '1', placeholder: 'e.g. 100',
                    value: settings.registrationReward,
                    onChange: function (e) { updateSettingField('registrationReward', e.target.value); },
                  }),
                  React.createElement('span', { className: 'sc-field-hint' }, 'New user will receive ' + (settings.registrationReward || 0) + ' ISHMAI Coins')
                ),
                React.createElement('div', { className: 'sc-field sc-field-toggle' },
                  React.createElement('label', null, 'Enable Registration Reward'),
                  React.createElement('label', { className: 'sc-toggle' },
                    React.createElement('input', {
                      type: 'checkbox', checked: !!settings.enableRegistrationReward,
                      onChange: function (e) { updateSettingField('enableRegistrationReward', e.target.checked); },
                    }),
                    React.createElement('span', { className: 'sc-toggle-track' }, React.createElement('span', { className: 'sc-toggle-thumb' }))
                  )
                ),
                React.createElement('div', { className: 'sc-field' },
                  React.createElement('label', null, 'Maximum Coins Per Order *'),
                  React.createElement('input', {
                    type: 'number', min: 0, step: '1', placeholder: 'e.g. 1000',
                    value: settings.maxCoinsPerOrder,
                    onChange: function (e) { updateSettingField('maxCoinsPerOrder', e.target.value); },
                  }),
                  React.createElement('span', { className: 'sc-field-hint' }, 'Maximum coins allowed per single order')
                ),
                React.createElement('div', { className: 'sc-field' },
                  React.createElement('label', null, 'Maximum Discount Percentage *'),
                  React.createElement('input', {
                    type: 'number', min: 0, max: 100, step: '1', placeholder: 'e.g. 20',
                    value: settings.maxDiscountPercentage !== undefined ? settings.maxDiscountPercentage : (settings.maxDiscountPercent || ''),
                    onChange: function (e) {
                      updateSettingField('maxDiscountPercentage', e.target.value);
                      updateSettingField('maxDiscountPercent', e.target.value);
                    },
                  }),
                  React.createElement('span', { className: 'sc-field-hint' }, 'Maximum discount allowed using ISHMAI Coins')
                ),
                React.createElement('div', { className: 'sc-field' },
                  React.createElement('label', null, 'Referral Coins *'),
                  React.createElement('input', {
                    type: 'number', min: 0, step: '1', placeholder: 'e.g. 100',
                    value: settings.referralReward !== undefined ? settings.referralReward : (settings.referralCoins !== undefined ? settings.referralCoins : ''),
                    onChange: function (e) {
                      updateSettingField('referralReward', e.target.value);
                      updateSettingField('referralCoins', e.target.value);
                    },
                  }),
                  React.createElement('span', { className: 'sc-field-hint' }, 'Coins rewarded to both referrer and referee')
                )
              ),
              React.createElement('div', { className: 'sc-form-actions sc-settings-actions' },
                React.createElement('button', {
                  className: 'sc-btn sc-btn-ghost', type: 'button', disabled: settingsSaving, onClick: handleResetSettings,
                }, 'Reset'),
                React.createElement('button', {
                  className: 'sc-btn sc-btn-primary', type: 'button', disabled: settingsSaving,
                  onClick: handleSaveSettings,
                }, settingsSaving ? 'Saving\u2026' : 'Save Settings')
              )
            )
      )
    );
  };

  // =========================================================================
  // Customers tab (list / add / details)
  // =========================================================================

  var renderCustomersList = function () {
    return React.createElement(React.Fragment, null,
      React.createElement(Breadcrumb, { items: [{ label: 'ISHMAI Coins', onClick: function () { goToTab('overview'); } }, { label: 'Customers' }] }),

      React.createElement('div', { className: 'sc-card' },
        React.createElement('div', { className: 'sc-card-hdr' },
          React.createElement('div', { className: 'sc-card-hdr-title' },
            React.createElement(UsersIcon, { size: 17 }),
            React.createElement('h3', null, 'Customer ISHMAI Coins')
          )
        ),

        customersError && React.createElement('div', { className: 'sc-error-banner' }, customersError),

        React.createElement('div', { className: 'sc-toolbar' },
          React.createElement('div', { className: 'sc-search-field' },
            React.createElement(SearchIcon, null),
            React.createElement('input', {
              placeholder: 'Search by name or email\u2026', value: search,
              onChange: function (e) { setSearch(e.target.value); },
            })
          ),
          React.createElement('button', { className: 'sc-btn sc-btn-ghost', type: 'button' }, React.createElement(FilterIcon, null), 'Filter'),
          React.createElement('div', { className: 'sc-toolbar-spacer' }),
          React.createElement('button', { className: 'sc-btn sc-btn-primary', type: 'button', onClick: goToAdd },
            React.createElement(PlusIcon, null), 'Add ISHMAI Coins')
        ),

        React.createElement('div', { className: 'sc-table-scroll' },
          React.createElement('table', { className: 'tbl' },
            React.createElement('thead', null,
              React.createElement('tr', null,
                React.createElement('th', null, 'S.No'),
                React.createElement('th', null, 'Customer Name'),
                React.createElement('th', null, 'Email'),
                React.createElement('th', null, 'Available ISHMAI Coins'),
                React.createElement('th', null, 'Coin Value (\u20B9)'),
                React.createElement('th', null, 'Total Earned'),
                React.createElement('th', null, 'Total Used'),
                React.createElement('th', null, 'Amount Used (\u20B9)'),
                React.createElement('th', null, 'Registration Reward'),
                React.createElement('th', null, 'Action')
              )
            ),
            React.createElement('tbody', null,
              customersLoading
                ? React.createElement('tr', null, React.createElement('td', { colSpan: 10, className: 'sc-table-empty' }, 'Loading customers\u2026'))
                : customers.length === 0
                  ? React.createElement('tr', null, React.createElement('td', { colSpan: 10, className: 'sc-table-empty' }, 'No customers found.'))
                  : customers.map(function (c, idx) {
                      var id = getCustId(c);
                      var name = getCustName(c);
                      var email = getCustEmail(c);
                      var available = getCustAvailable(c);
                      return React.createElement('tr', { key: id || idx },
                        React.createElement('td', { style: { fontSize: 12 } }, ((page - 1) * perPage) + idx + 1),
                        React.createElement('td', null,
                          React.createElement('div', { className: 'sc-cust-cell' },
                            React.createElement('div', { className: 'sc-avatar' }, (name || 'C')[0].toUpperCase()),
                            React.createElement('span', { style: { fontWeight: 700 } }, name)
                          )
                        ),
                        React.createElement('td', null, email),
                        React.createElement('td', null, React.createElement('span', { className: 'sc-badge sc-badge-available' }, formatCoins(available))),
                        React.createElement('td', null, formatINR(getCustValue(c, coinsPerRupee))),
                        React.createElement('td', { className: 'sc-coins-pos' }, formatCoins(getCustEarned(c))),
                        React.createElement('td', { className: 'sc-coins-neg' }, formatCoins(getCustUsed(c))),
                        React.createElement('td', null, formatINR(getCustAmountUsed(c, coinsPerRupee))),
                        React.createElement('td', null, React.createElement('span', { className: 'sc-badge sc-badge-earned' }, formatCoins(getCustRegReward(c)))),
                        React.createElement('td', { className: 'actions-cell' },
                          React.createElement('button', {
                            className: 'sc-btn sc-btn-outline sc-btn-sm', type: 'button',
                            onClick: function () { goToDetails(c); },
                          }, 'View Details')
                        )
                      );
                    })
            )
          )
        ),

        !customersLoading && customers.length > 0 && React.createElement('div', { className: 'sc-table-footer' },
          React.createElement('span', { className: 'sc-pagination-info' },
            'Showing ', ((page - 1) * perPage) + 1, ' to ', ((page - 1) * perPage) + customers.length, ' of ', customersTotal, ' customers'
          ),
          React.createElement(Pagination, { page: page, pages: customersPages, onChange: setPage }),
          React.createElement('div', { className: 'sc-rows-per-page' },
            React.createElement('span', null, 'Rows per page:'),
            React.createElement('select', {
              value: perPage,
              onChange: function (e) { setPerPage(Number(e.target.value)); setPage(1); },
            },
              CUSTOMERS_PER_PAGE_OPTIONS.map(function (n) { return React.createElement('option', { key: n, value: n }, n); })
            )
          )
        )
      )
    );
  };

  var renderAddCoins = function () {
    return React.createElement(AddSuperCoinsPage, {
      coinsPerRupee: coinsPerRupee,
      onBack: goToCustomersList,
      onAdded: function (info) {
        setSuccessModal({
          isOpen: true,
          message: (info && info.message ? String(info.message).replace(/Super Coins/gi, 'ISHMAI Coins') : 'ISHMAI Coins Added Successfully'),
          subtitle: (info && info.subtitle) || 'The coins have been added to the customer successfully.',
        });
        refreshAll();
        goToCustomersList();
      },
    });
  };

  var renderCustomerDetails = function () {
    return React.createElement(CustomerDetailsPage, {
      customerId: activeCustomerId,
      customerData: activeCustomerData,
      coinsPerRupee: coinsPerRupee,
      onBack: goToCustomersList,
      onRefundSuccess: function (msg) {
        setSuccessModal({
          isOpen: true,
          message: 'ISHMAI Coins Refunded Successfully',
          subtitle: (msg ? String(msg).replace(/Super Coins/gi, 'ISHMAI Coins') : 'ISHMAI Coins refunded/reversed for order successfully'),
        });
        refreshAll();
      },
    });
  };

  var renderCustomersTab = function () {
    if (customerView === 'add') return renderAddCoins();
    if (customerView === 'details') return renderCustomerDetails();
    return renderCustomersList();
  };

  return React.createElement(AdminLayout, null,
    React.createElement(Toast, { toast: toast, onClose: function () { setToast(null); } }),
    successModal && React.createElement(UpdateSuccessModal, {
      isOpen: true,
      message: successModal.message || 'Success',
      subtitle: successModal.subtitle || '',
      onClose: function () { setSuccessModal(null); }
    }),

    React.createElement('div', { className: 'page-header-row' },
      React.createElement('div', { className: 'page-header-main' },
        React.createElement('div', { className: 'page-header-title-row' },
          React.createElement('div', { className: 'page-header-icon' }, React.createElement(CoinIcon, { size: 18 })),
          React.createElement('div', { className: 'ptitle' }, 'ISHMAI Coins Management')
        ),
        React.createElement('div', { className: 'header-subtitle' }, 'Manage ISHMAI Coins system, settings and customer details')
      )
    ),

    React.createElement(TabBar, { active: activeTab, onChange: goToTab }),

    activeTab === 'overview' && renderOverview(),
    activeTab === 'settings' && renderSettingsTab(),
    activeTab === 'customers' && renderCustomersTab()
  );
};

// ---------------------------------------------------------------------------
// Add Super Coins page (nested under the Customers tab)
// ---------------------------------------------------------------------------

var AddSuperCoinsPage = function (props) {
  var coinsPerRupee = props.coinsPerRupee; var onBack = props.onBack; var onAdded = props.onAdded;

  var wrapperRef = useRef(null);
  var queryState = useState(''); var query = queryState[0]; var setQuery = queryState[1];
  var queryDebState = useState(''); var queryDeb = queryDebState[0]; var setQueryDeb = queryDebState[1];
  var resultsState = useState([]); var results = resultsState[0]; var setResults = resultsState[1];
  var searchingState = useState(false); var searching = searchingState[0]; var setSearching = searchingState[1];
  var dropdownOpenState = useState(false); var dropdownOpen = dropdownOpenState[0]; var setDropdownOpen = dropdownOpenState[1];

  var selectedState = useState(null); var selected = selectedState[0]; var setSelected = selectedState[1];
  var coinsToAddState = useState(''); var coinsToAdd = coinsToAddState[0]; var setCoinsToAdd = coinsToAddState[1];
  var reasonState = useState(''); var reason = reasonState[0]; var setReason = reasonState[1];

  var errorState = useState(''); var formError = errorState[0]; var setFormError = errorState[1];
  var savingState = useState(false); var saving = savingState[0]; var setSaving = savingState[1];

  useEffect(function () {
    var t = setTimeout(function () { setQueryDeb(query); }, 300);
    return function () { clearTimeout(t); };
  }, [query]);

  useEffect(function () {
    var handleClickOutside = function (e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  useEffect(function () {
    setSearching(true);
    getCustomerSuperCoins({ search: queryDeb.trim() || undefined, page: 1, limit: 10 })
      .then(function (res) {
        var body = res && res.data;
        var list = (body && Array.isArray(body.data)) ? body.data : ((body && body.data && body.data.list) || (Array.isArray(body) ? body : []));
        setResults(list);
      })
      .catch(function (e) { console.error('Customer search failed:', e); setResults([]); })
      .finally(function () { setSearching(false); });
  }, [queryDeb]);

  var selectCustomer = function (c) {
    setSelected(c);
    setQuery(getCustName(c));
    setDropdownOpen(false);
    setFormError('');
  };

  var availableCoins = getCustAvailable(selected);
  var addedCoinsNum = Number(coinsToAdd) || 0;
  var rupeeValue = coinsToRupees(addedCoinsNum, coinsPerRupee);

  var validate = function () {
    if (!selected) return 'Please select a customer';
    if (!coinsToAdd || isNaN(addedCoinsNum) || addedCoinsNum <= 0) return 'Enter a valid number of coins to add';
    if (!reason.trim()) return 'Please enter a reason for adding coins';
    return '';
  };

  var handleSubmit = function () {
    var err = validate();
    if (err) { setFormError(err); return; }
    setFormError('');
    setSaving(true);
    var custId = getCustId(selected);
    adjustCustomerSuperCoins({
      customerId: custId,
      coins: addedCoinsNum,
      type: 'credit',
      reason: reason.trim(),
    })
      .then(function (res) {
        var msg = (res && res.data && (res.data.message || res.data.msg)) || 'ISHMAI Coins credited successfully';
        onAdded({
          message: 'ISHMAI Coins Added Successfully',
          subtitle: formatCoins(addedCoinsNum) + ' ISHMAI Coins have been credited to ' + getCustName(selected) + ' successfully.',
        });
      })
      .catch(function (e) {
        var msg = getErrorMessage(e, 'Failed to add ISHMAI Coins');
        setFormError(msg);
      })
      .finally(function () { setSaving(false); });
  };

  return React.createElement(React.Fragment, null,
    React.createElement(Breadcrumb, {
      items: [{ label: 'ISHMAI Coins' }, { label: 'Customers', onClick: onBack }, { label: 'Add ISHMAI Coins' }],
    }),

    React.createElement('button', { className: 'sc-back-link', type: 'button', onClick: onBack },
      React.createElement(ArrowLeftIcon, null), ' Back to Customers'
    ),

    React.createElement('div', { className: 'sc-card sc-add-card' },
      formError && React.createElement('div', { className: 'sc-form-error' }, React.createElement(AlertCircleIcon, null), formError),

      React.createElement('div', { className: 'sc-settings-grid' },
        React.createElement('div', { className: 'sc-field sc-field-typeahead', ref: wrapperRef },
          React.createElement('label', null, 'Select Customer *'),
          React.createElement('div', { className: 'sc-typeahead' },
            React.createElement(SearchIcon, null),
            React.createElement('input', {
              placeholder: 'Search customer by name or email\u2026',
              value: query,
              onChange: function (e) { setQuery(e.target.value); setSelected(null); setDropdownOpen(true); },
              onFocus: function () { setDropdownOpen(true); },
            })
          ),
          dropdownOpen && React.createElement('div', { className: 'sc-typeahead-dd' },
            searching
              ? React.createElement('div', { className: 'sc-typeahead-empty' }, 'Searching\u2026')
              : results.length === 0
                ? React.createElement('div', { className: 'sc-typeahead-empty' }, 'No matching customers')
                : results.map(function (c, idx) {
                    return React.createElement('div', {
                      key: getCustId(c) || idx, className: 'sc-typeahead-opt',
                      onClick: function () { selectCustomer(c); },
                    },
                      React.createElement('span', { style: { fontWeight: 700 } }, getCustName(c)),
                      React.createElement('span', { className: 'sc-typeahead-email' }, getCustEmail(c))
                    );
                  })
          )
        ),
        React.createElement('div', { className: 'sc-field' },
          React.createElement('label', null, 'Customer Email'),
          React.createElement('input', { value: getCustEmail(selected), readOnly: true, placeholder: 'customer@email.com', className: 'sc-readonly' })
        ),
        React.createElement('div', { className: 'sc-field' },
          React.createElement('label', null, 'Current ISHMAI Coins'),
          React.createElement('input', { value: selected ? formatCoins(availableCoins) : '', readOnly: true, placeholder: '\u2014', className: 'sc-readonly' })
        ),
        React.createElement('div', { className: 'sc-field' },
          React.createElement('label', null, 'Coins to Add *'),
          React.createElement('input', {
            type: 'number', min: 1, placeholder: 'Enter coins to add',
            value: coinsToAdd, onChange: function (e) { setCoinsToAdd(e.target.value); },
          })
        ),
        React.createElement('div', { className: 'sc-field' },
          React.createElement('label', null, '\u20B9 Value'),
          React.createElement('input', { value: addedCoinsNum > 0 ? formatINR(rupeeValue) : '\u20B90.00', readOnly: true, className: 'sc-readonly' })
        ),
        React.createElement('div', { className: 'sc-field sc-field-full' },
          React.createElement('label', null, 'Reason *'),
          React.createElement('input', {
            placeholder: 'Enter reason for adding coins',
            value: reason, onChange: function (e) { setReason(e.target.value); },
          })
        )
      ),

      React.createElement('div', { className: 'sc-form-actions sc-add-actions' },
        React.createElement('button', { className: 'sc-btn sc-btn-ghost', type: 'button', onClick: onBack, disabled: saving }, 'Cancel'),
        React.createElement('button', {
          className: 'sc-btn sc-btn-primary', type: 'button', disabled: saving, onClick: handleSubmit,
        }, saving ? 'Adding\u2026' : 'Add ISHMAI Coins')
      )
    )
  );
};

// ---------------------------------------------------------------------------
// Customer Super Coins Details page (nested under the Customers tab)
// ---------------------------------------------------------------------------

var CustomerDetailsPage = function (props) {
  var customerId = props.customerId;
  var customerData = props.customerData;
  var coinsPerRupee = props.coinsPerRupee;
  var onBack = props.onBack;
  var onRefundSuccess = props.onRefundSuccess;

  var customerState = useState(customerData || null); var customer = customerState[0]; var setCustomer = customerState[1];
  var customerLoadingState = useState(!customerData); var customerLoading = customerLoadingState[0]; var setCustomerLoading = customerLoadingState[1];

  var historyState = useState([]); var history = historyState[0]; var setHistory = historyState[1];
  var historyTotalState = useState(0); var historyTotal = historyTotalState[0]; var setHistoryTotal = historyTotalState[1];
  var historyLoadingState = useState(true); var historyLoading = historyLoadingState[0]; var setHistoryLoading = historyLoadingState[1];
  var historyErrorState = useState(''); var historyError = historyErrorState[0]; var setHistoryError = historyErrorState[1];
  var historyPageState = useState(1); var historyPage = historyPageState[0]; var setHistoryPage = historyPageState[1];

  var refundTargetState = useState(null); var refundTarget = refundTargetState[0]; var setRefundTarget = refundTargetState[1];
  var refundingState = useState(false); var refunding = refundingState[0]; var setRefunding = refundingState[1];
  var refundErrorState = useState(''); var refundError = refundErrorState[0]; var setRefundError = refundErrorState[1];

  useEffect(function () {
    if (!customerId) return;
    setCustomerLoading(true);
    getCustomerSuperCoinDetails(customerId)
      .then(function (res) {
        var d = (res && res.data && res.data.data) ? res.data.data : (res && res.data) || null;
        if (d) setCustomer(d);
      })
      .catch(function () {
        // Fallback to customerData passed via props
      })
      .finally(function () { setCustomerLoading(false); });
  }, [customerId]);

  useEffect(function () {
    if (!customerId) return;
    setHistoryLoading(true);
    setHistoryError('');
    getCustomerSuperCoinHistory(customerId, { page: historyPage, limit: HISTORY_PER_PAGE })
      .then(function (res) {
        var body = res && res.data;
        var list = (body && Array.isArray(body.data)) ? body.data : ((body && body.data && body.data.list) || (Array.isArray(body) ? body : []));
        var totalCount = (body && body.total !== undefined) ? body.total : (body && body.count !== undefined ? body.count : list.length);
        setHistory(list);
        setHistoryTotal(totalCount);
      })
      .catch(function (e) {
        setHistoryError(getErrorMessage(e, 'Failed to load transaction history.'));
        setHistory([]);
      })
      .finally(function () { setHistoryLoading(false); });
  }, [customerId, historyPage]);

  var handleConfirmRefund = function () {
    if (!refundTarget) return;
    setRefunding(true);
    setRefundError('');
    refundSuperCoins({ orderId: refundTarget })
      .then(function (res) {
        var msg = (res && res.data && (res.data.message || res.data.msg)) || 'ISHMAI Coins refunded/reversed for order successfully';
        setRefundTarget(null);
        if (onRefundSuccess) onRefundSuccess(msg);
        getCustomerSuperCoinHistory(customerId, { page: historyPage, limit: HISTORY_PER_PAGE })
          .then(function (hRes) {
            var b = hRes && hRes.data;
            var l = (b && Array.isArray(b.data)) ? b.data : ((b && b.data && b.data.list) || (Array.isArray(b) ? b : []));
            setHistory(l);
          });
        getCustomerSuperCoinDetails(customerId)
          .then(function (dRes) {
            var cd = (dRes && dRes.data && dRes.data.data) ? dRes.data.data : (dRes && dRes.data) || null;
            if (cd) setCustomer(cd);
          });
      })
      .catch(function (e) {
        setRefundError(getErrorMessage(e, 'Failed to refund ISHMAI Coins'));
      })
      .finally(function () { setRefunding(false); });
  };

  var custObj = (customer && customer.customer) || (customer && customer.customerId && typeof customer.customerId === 'object' ? customer.customerId : customer) || (customerData && customerData.customerId && typeof customerData.customerId === 'object' ? customerData.customerId : customerData) || {};
  var balObj = (customer && customer.balance) || customer || customerData || {};
  var custName = custObj.name || custObj.fullName || (customerData ? getCustName(customerData) : 'Customer');
  var custEmail = custObj.email || (customerData ? getCustEmail(customerData) : '—');
  var joinedAt = custObj.createdAt || custObj.joinedAt || (customerData && (customerData.createdAt || (customerData.customerId && customerData.customerId.createdAt)));

  var available = balObj.availableCoins !== undefined
    ? Number(balObj.availableCoins)
    : (balObj.availableSuperCoins !== undefined
        ? Number(balObj.availableSuperCoins)
        : (customerData ? getCustAvailable(customerData) : 0));

  var totalEarned = balObj.totalEarned !== undefined
    ? Number(balObj.totalEarned)
    : (customerData ? getCustEarned(customerData) : 0);

  var totalUsed = balObj.totalUsed !== undefined
    ? Number(balObj.totalUsed)
    : (customerData ? getCustUsed(customerData) : 0);

  var rupeeVal = balObj.rupeeValue !== undefined
    ? Number(balObj.rupeeValue)
    : (customerData && customerData.rupeeValue !== undefined
        ? Number(customerData.rupeeValue)
        : coinsToRupees(available, coinsPerRupee));

  var amountUsedVal = balObj.totalUsedRupeeValue !== undefined
    ? Number(balObj.totalUsedRupeeValue)
    : coinsToRupees(totalUsed, coinsPerRupee);

  var regRewardTx = (history || []).find(function (tx) {
    var t = String(tx.type || '').toLowerCase();
    var d = String(tx.description || '').toLowerCase();
    return t === 'registration_reward' || t === 'reward' || t.includes('registration') || d.includes('registration');
  });
  var regReward = (regRewardTx && regRewardTx.coins !== undefined)
    ? Math.abs(Number(regRewardTx.coins))
    : (balObj.registrationReward !== undefined
        ? Number(balObj.registrationReward)
        : (custObj.registrationReward !== undefined
            ? Number(custObj.registrationReward)
            : (customerData && customerData.registrationReward !== undefined
                ? Number(customerData.registrationReward)
                : 0)));

  var displayHistory = (history && history.length > 0) ? history : (customer && customer.recentTransactions ? customer.recentTransactions : []);
  var historyPages = totalPages(historyTotal, HISTORY_PER_PAGE);

  return React.createElement(React.Fragment, null,
    React.createElement(Breadcrumb, {
      items: [{ label: 'ISHMAI Coins' }, { label: 'Customers', onClick: onBack }, { label: 'Customer Details' }],
    }),

    React.createElement('button', { className: 'sc-back-link', type: 'button', onClick: onBack },
      React.createElement(ArrowLeftIcon, null), ' Back to Customers'
    ),

    customerLoading && !customer
      ? React.createElement('div', { className: 'sc-table-empty' }, 'Loading customer details\u2026')
      : React.createElement(React.Fragment, null,

          React.createElement('div', { className: 'sc-card sc-details-hdr-card' },
            React.createElement('div', { className: 'sc-details-hdr' },
              React.createElement('div', { className: 'sc-avatar sc-avatar-lg' }, (custName || 'C')[0].toUpperCase()),
              React.createElement('div', null,
                React.createElement('div', { className: 'sc-details-name' }, custName),
                React.createElement('div', { className: 'sc-details-email' }, custEmail),
                custObj.referralCode && React.createElement('div', { className: 'sc-details-joined', style: { color: '#6d28d9', fontWeight: 700, marginTop: '2px' } }, 'Referral Code: ', custObj.referralCode),
                joinedAt && React.createElement('div', { className: 'sc-details-joined' }, 'Joined on ', formatDateShort(joinedAt))
              )
            ),
            React.createElement('div', { className: 'sc-details-stat-grid' },
              React.createElement('div', { className: 'sc-details-stat' },
                React.createElement('span', null, 'Current Balance'),
                React.createElement('strong', null, formatCoins(available), ' ISHMAI Coins'),
                React.createElement('small', null, formatINR(rupeeVal))
              ),
              React.createElement('div', { className: 'sc-details-stat' },
                React.createElement('span', null, 'Total Earned'),
                React.createElement('strong', null, formatCoins(totalEarned), ' ISHMAI Coins'),
                React.createElement('small', null, formatINR(coinsToRupees(totalEarned, coinsPerRupee)))
              ),
              React.createElement('div', { className: 'sc-details-stat' },
                React.createElement('span', null, 'Total Used'),
                React.createElement('strong', null, formatCoins(totalUsed), ' ISHMAI Coins'),
                React.createElement('small', null, formatINR(amountUsedVal))
              ),
              React.createElement('div', { className: 'sc-details-stat' },
                React.createElement('span', null, 'Registration Reward'),
                React.createElement('strong', null, formatCoins(regReward), ' ISHMAI Coins'),
                React.createElement('small', null, formatINR(coinsToRupees(regReward, coinsPerRupee)))
              )
            )
          ),

          React.createElement('div', { className: 'sc-card' },
            React.createElement('div', { className: 'sc-card-hdr' },
              React.createElement('div', { className: 'sc-card-hdr-title' }, React.createElement('h3', null, 'Transaction History')),
              React.createElement('button', { className: 'sc-btn sc-btn-ghost sc-btn-sm', type: 'button' }, React.createElement(FilterIcon, null), 'Filter')
            ),

            historyError && React.createElement('div', { className: 'sc-error-banner' }, historyError),

            React.createElement('div', { className: 'sc-table-scroll' },
              React.createElement('table', { className: 'tbl' },
                React.createElement('thead', null,
                  React.createElement('tr', null,
                    React.createElement('th', null, 'Date'),
                    React.createElement('th', null, 'Type'),
                    React.createElement('th', null, 'Coins'),
                    React.createElement('th', null, '\u20B9 Amount'),
                    React.createElement('th', null, 'Description'),
                    React.createElement('th', null, 'Order ID'),
                    React.createElement('th', null, 'Action')
                  )
                ),
                React.createElement('tbody', null,
                  historyLoading
                    ? React.createElement('tr', null, React.createElement('td', { colSpan: 7, className: 'sc-table-empty' }, 'Loading transaction history\u2026'))
                    : displayHistory.length === 0
                      ? React.createElement('tr', null, React.createElement('td', { colSpan: 7, className: 'sc-table-empty' }, 'No transactions found for this customer.'))
                      : displayHistory.map(function (tx, idx) {
                          var rawType = String(tx.type || 'earned').toLowerCase();
                          var isUsed = rawType === 'used' || rawType === 'debit' || rawType === 'redeemed' || rawType === 'manual_debit';
                          var isReg = rawType === 'registration_reward' || rawType === 'reward' || rawType.includes('registration');
                          var txCoins = Math.abs(Number(tx.coins || 0));
                          var txDate = tx.createdAt || tx.date || tx.timestamp;
                          var txRupees = tx.rupeeValue !== undefined ? Number(tx.rupeeValue) : (tx.amount !== undefined ? Number(tx.amount) : coinsToRupees(txCoins, coinsPerRupee));
                          var txOrder = (tx.orderId && typeof tx.orderId === 'object') ? (tx.orderId._id || tx.orderId.id) : (tx.orderId || null);
                          var txDesc = tx.description || tx.reason || (isReg ? 'Registration Reward' : '\u2014');
                          var canRefund = txOrder && (isUsed || rawType === 'redeemed');

                          var typeBadgeLabel = isReg ? 'Registration Reward'
                            : rawType === 'manual_credit' ? 'Manual Credit'
                            : rawType === 'manual_debit' ? 'Manual Debit'
                            : rawType === 'redeemed' ? 'Redeemed'
                            : isUsed ? 'Debit'
                            : 'Credit';

                          return React.createElement('tr', { key: tx._id || tx.id || idx },
                            React.createElement('td', null, formatDateTime(txDate)),
                            React.createElement('td', null,
                              React.createElement('span', { className: 'sc-badge ' + (isUsed ? 'sc-badge-used' : 'sc-badge-earned') }, typeBadgeLabel)
                            ),
                            React.createElement('td', { className: isUsed ? 'sc-coins-neg' : 'sc-coins-pos' }, (isUsed ? '\u2212' : '+') + formatCoins(txCoins)),
                            React.createElement('td', null, formatINR(txRupees)),
                            React.createElement('td', { className: 'desc-cell', title: txDesc }, txDesc),
                            React.createElement('td', null, txOrder || '\u2014'),
                            React.createElement('td', { className: 'actions-cell' },
                              canRefund
                                ? React.createElement('button', {
                                    className: 'sc-btn sc-btn-outline sc-btn-sm',
                                    type: 'button',
                                    onClick: function () { setRefundTarget(txOrder); setRefundError(''); }
                                  }, 'Refund')
                                : React.createElement('span', { className: 'bdg bdg-g' }, tx.status || 'Completed')
                            )
                          );
                        })
                )
              )
            ),

            !historyLoading && displayHistory.length > 0 && React.createElement('div', { className: 'sc-table-footer' },
              React.createElement('span', { className: 'sc-pagination-info' },
                'Showing ', ((historyPage - 1) * HISTORY_PER_PAGE) + 1, ' to ', ((historyPage - 1) * HISTORY_PER_PAGE) + displayHistory.length, ' of ', (historyTotal || displayHistory.length), ' transactions'
              ),
              React.createElement(Pagination, { page: historyPage, pages: historyPages, onChange: setHistoryPage })
            )
          ),

          // Refund confirmation modal
          refundTarget && React.createElement('div', {
            className: 'modal-overlay',
            style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(18, 10, 58, 0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }
          },
            React.createElement('div', {
              style: { background: '#ffffff', borderRadius: 16, padding: '24px', maxWidth: 440, width: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }
            },
              React.createElement('h3', { style: { margin: '0 0 10px', fontSize: 18, color: '#0f172a' } }, 'Refund ISHMAI Coins'),
              React.createElement('p', { style: { margin: '0 0 16px', fontSize: 13.5, color: '#475569', lineHeight: 1.5 } },
                'Are you sure you want to refund/reverse ISHMAI Coins for Order #' + refundTarget + '?'
              ),
              refundError && React.createElement('div', { className: 'sc-form-error', style: { marginBottom: 14 } }, refundError),
              React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 10 } },
                React.createElement('button', { className: 'sc-btn sc-btn-ghost', type: 'button', disabled: refunding, onClick: function () { setRefundTarget(null); setRefundError(''); } }, 'Cancel'),
                React.createElement('button', { className: 'sc-btn sc-btn-primary', type: 'button', disabled: refunding, onClick: handleConfirmRefund }, refunding ? 'Refunding\u2026' : 'Confirm Refund')
              )
            )
          )
        )
  );
};

export default SuperCoins;